import * as THREE from "three";
import { getSceneState } from "../../stores/sceneStore";
import type { BodyUUID, SiteUUID } from "../../types/scene";
import * as RuntimeRegistry from "../../runtime/objectRegistry";
import { logger } from "../../utils/logger";

const GRIPPER_TARGET_SITE_NAME = "gripper_target";

export const findGripperTargetOffset = (
  scene: ReturnType<typeof getSceneState>,
  endEffectorUuid: BodyUUID,
  yToZ: THREE.Quaternion,
  logWarnings: boolean
) => {
  const registry = RuntimeRegistry.getAll() || {};
  const siteEntries = Object.values(scene.sites || {});
  const site = siteEntries.find((entry) => entry?.name === GRIPPER_TARGET_SITE_NAME) || null;
  if (!site) {
    if (logWarnings) {
      logger.warn("[ik] gripper_target site not found in scene");
    }
    return null;
  }
  const siteUuid = site.uuid as SiteUUID;
  const bodyEntry = Object.values(scene.bodies || {}).find((b) =>
    Array.isArray(b?.sites) ? b.sites.includes(siteUuid) : false
  );
  if (!bodyEntry) {
    if (logWarnings) {
      logger.warn("[ik] Unable to resolve gripper_target site body");
    }
    return null;
  }
  const sitePos =
    Array.isArray(site.position) && site.position.length >= 3 ? site.position : [0, 0, 0];
  const siteQuat =
    Array.isArray(site.orientation) && site.orientation.length >= 4
      ? site.orientation
      : [1, 0, 0, 0];
  const siteLocalQuat = new THREE.Quaternion(
    siteQuat[1],
    siteQuat[2],
    siteQuat[3],
    siteQuat[0]
  ).normalize();

  const fallbackAxesFromLocalSiteQuat = () => {
    // Interpret the site orientation as authored in the IK chain (MuJoCo / Z-up) frame.
    // This is correct when the site lives on the same body as the IK end-effector.
    const forwardLocalZFrame = new THREE.Vector3(0, 0, 1)
      .applyQuaternion(siteLocalQuat)
      .normalize();
    const upLocalZFrame = new THREE.Vector3(0, 1, 0).applyQuaternion(siteLocalQuat).normalize();
    return { forwardLocalZFrame, upLocalZFrame };
  };
  if (bodyEntry.uuid === endEffectorUuid) {
    const offsetLocal = new THREE.Vector3(sitePos[0], sitePos[1], sitePos[2]);
    // Site positions are stored in MuJoCo/local coordinates already (Z-up chain frame),
    // so do NOT apply the scene Y-up -> chain Z-up rotation here.
    //
    // Derive the approach direction from the position offset, not the site's quat rotation.
    // The offset vector points from the end-effector origin to the gripper tip, which IS the
    // gripper approach axis. Using the site quat instead can give a wrong axis (e.g. the Panda
    // gripper_target site has a 90-degree X rotation that would make the arm approach sideways).
    let forwardLocalZFrame: THREE.Vector3;
    let upLocalZFrame: THREE.Vector3;
    if (offsetLocal.lengthSq() > 1e-10) {
      forwardLocalZFrame = offsetLocal.clone().normalize();
      // Build an up vector perpendicular to forward using a stable world reference.
      const ref =
        Math.abs(forwardLocalZFrame.z) < 0.9
          ? new THREE.Vector3(0, 0, 1)
          : new THREE.Vector3(0, 1, 0);
      upLocalZFrame = ref
        .clone()
        .sub(forwardLocalZFrame.clone().multiplyScalar(ref.dot(forwardLocalZFrame)))
        .normalize();
    } else {
      ({ forwardLocalZFrame, upLocalZFrame } = fallbackAxesFromLocalSiteQuat());
    }
    return {
      offsetZFrame: offsetLocal,
      forwardLocalZFrame,
      upLocalZFrame,
      siteName: GRIPPER_TARGET_SITE_NAME,
      siteBodyUuid: bodyEntry.uuid,
    };
  }

  const bodyObj = registry[bodyEntry.uuid] as THREE.Object3D | undefined;
  if (!bodyObj) {
    if (logWarnings) {
      logger.warn("[ik] Unable to locate runtime body for gripper_target site", {
        bodyUuid: bodyEntry.uuid,
      });
    }
    return null;
  }
  const siteWorld = new THREE.Vector3(sitePos[0], sitePos[1], sitePos[2]);
  bodyObj.localToWorld(siteWorld);

  const effObj = registry[endEffectorUuid] as THREE.Object3D | undefined;
  if (!effObj) {
    if (logWarnings) {
      logger.warn("[ik] Unable to locate runtime end-effector for gripper_target offset", {
        endEffectorUuid,
      });
    }
    return null;
  }
  const effPos = new THREE.Vector3();
  const effQuat = new THREE.Quaternion();
  effObj.getWorldPosition(effPos);
  effObj.getWorldQuaternion(effQuat);

  // Compute the site axes expressed in the end-effector local frame (then convert to Z-up chain frame).
  // If we only use the site's local quat without accounting for the mount transform between bodies,
  // the IK end-effector can approach "sideways" when the site is on a different body (e.g. gripper base).
  let forwardLocalZFrame: THREE.Vector3 | null = null;
  let upLocalZFrame: THREE.Vector3 | null = null;
  try {
    const bodyWorldQuat = new THREE.Quaternion();
    bodyObj.getWorldQuaternion(bodyWorldQuat);
    const siteWorldQuat = bodyWorldQuat.clone().multiply(siteLocalQuat).normalize();
    const forwardWorldScene = new THREE.Vector3(0, 0, 1).applyQuaternion(siteWorldQuat).normalize();
    const upWorldScene = new THREE.Vector3(0, 1, 0).applyQuaternion(siteWorldQuat).normalize();
    const invEff = effQuat.clone().invert();
    const forwardLocalScene = forwardWorldScene.clone().applyQuaternion(invEff).normalize();
    const upLocalScene = upWorldScene.clone().applyQuaternion(invEff).normalize();
    forwardLocalZFrame = forwardLocalScene.clone().applyQuaternion(yToZ).normalize();
    upLocalZFrame = upLocalScene.clone().applyQuaternion(yToZ).normalize();
  } catch {
    // ignore; fall back below
  }

  const offsetLocal = siteWorld.clone().sub(effPos).applyQuaternion(effQuat.clone().invert());
  const offsetZFrame = offsetLocal.clone().applyQuaternion(yToZ);
  return {
    offsetZFrame,
    ...(forwardLocalZFrame && upLocalZFrame
      ? { forwardLocalZFrame, upLocalZFrame }
      : fallbackAxesFromLocalSiteQuat()),
    siteName: GRIPPER_TARGET_SITE_NAME,
    siteBodyUuid: bodyEntry.uuid,
  };
};
